export interface BlogPost {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  date: string; // ISO
  excerpt: string;
  keywords: string[];
  /** Markdown-lite: paragraphs separated by blank lines; ## for H2; - for bullet lists */
  content: string;
  relatedServiceSlug?: string;
}

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "ibs-dietitian-south-africa-low-fodmap",
    title: "IBS in South Africa: A Dietitian's Guide to Managing Symptoms with the Low-FODMAP Diet",
    metaTitle: "IBS Dietitian South Africa | Low-FODMAP Guide | Functional Foodie",
    metaDescription: "A registered dietitian in Johannesburg explains how the low-FODMAP diet can help manage IBS symptoms — bloating, cramping, and irregular bowel habits.",
    category: "Gut Health",
    date: "2026-06-10",
    excerpt: "Living with IBS in South Africa? The low-FODMAP diet, guided by a registered dietitian, is one of the most evidence-based ways to settle bloating, cramping and unpredictable bowel habits.",
    keywords: ["IBS dietitian South Africa", "low FODMAP diet Johannesburg", "IBS treatment", "gut health"],
    relatedServiceSlug: "gut-health",
    content: `Irritable Bowel Syndrome (IBS) affects up to one in five South Africans at some point in their lives, yet many people quietly live with bloating, cramping and unpredictable bowel habits because they assume "that's just how my gut is." It doesn't have to be.

As a registered dietitian based in Johannesburg, IBS is one of the conditions I see most often in practice. The good news: with the right approach, the majority of clients see meaningful improvement within four to six weeks.

## What is IBS?

IBS is a functional gut disorder, meaning the gut looks structurally normal on a scope but doesn't behave normally. Symptoms typically include abdominal pain or discomfort, bloating, excess wind, and a change in bowel habits — diarrhoea, constipation, or alternating between the two.

IBS is a diagnosis of exclusion. Before starting any dietary intervention, your GP or gastroenterologist should rule out coeliac disease, inflammatory bowel disease and other conditions that share similar symptoms.

## What is the low-FODMAP diet?

FODMAPs are Fermentable Oligosaccharides, Disaccharides, Monosaccharides And Polyols — short-chain carbohydrates that are poorly absorbed in the small intestine. In sensitive guts, they pull water into the bowel and are rapidly fermented by gut bacteria, producing gas and triggering the classic IBS symptoms.

Common high-FODMAP foods in the South African diet include onion, garlic, wheat (so most local breads and pasta), apples, pears, mango, dairy milk, legumes, honey, and sugar-free sweeteners ending in -ol.

## The three phases

The low-FODMAP diet is not a lifelong restriction. It runs in three phases:

- Phase 1 — Elimination (2–6 weeks): all high-FODMAP foods are removed.
- Phase 2 — Reintroduction (6–8 weeks): foods are systematically reintroduced one FODMAP group at a time to identify personal triggers.
- Phase 3 — Personalisation: a long-term, as-liberal-as-possible diet built around your individual tolerances.

Skipping the reintroduction phase is the most common mistake people make on their own. A permanently restricted diet harms the gut microbiome and is unnecessary — most people only react to one or two FODMAP groups.

## Why work with a registered dietitian?

The low-FODMAP diet is complex, and the Monash University research it's based on is updated regularly. Going it alone often leads to over-restriction, nutrient gaps, and ultimately giving up.

At Functional Foodie I guide clients through all three phases, adapt the food lists to what's actually available in South African supermarkets, and combine the protocol with stress, sleep and meal-timing strategies that often matter just as much as the food itself.

If you've been battling with IBS, [book a gut health consultation](/booking) and let's build a plan that fits your life.`,
  },
  {
    slug: "bloating-reflux-when-to-see-dietitian-johannesburg",
    title: "Bloating, Reflux & Gut Issues: When to See a Registered Dietitian in Johannesburg",
    metaTitle: "Bloating & Reflux Dietitian Johannesburg | Functional Foodie",
    metaDescription: "Persistent bloating or reflux? A registered dietitian in Johannesburg explains the warning signs that mean it's time to get expert help.",
    category: "Gut Health",
    date: "2026-06-08",
    excerpt: "Occasional bloating is normal. Daily bloating, painful reflux or a gut that dictates your social life is not. Here's when to stop self-treating and see a dietitian.",
    keywords: ["bloating dietitian", "gut health Johannesburg", "reflux dietitian"],
    relatedServiceSlug: "gut-health",
    content: `Almost every adult experiences bloating or reflux occasionally. The problem starts when "occasionally" becomes "most days" — and when you start planning your outfits, meals and outings around your gut.

## Bloating: normal vs not normal

Mild bloating after a large meal, especially one high in fibre or salt, is completely normal. What isn't normal:

- Visible distension that lasts for hours
- Bloating accompanied by pain or cramping
- Bloating that's getting worse over months
- Bloating with weight loss, blood in your stool, or night-time symptoms

The last group are "red flag" symptoms and warrant a GP visit before any dietary work.

## Reflux: more than just spicy food

Reflux (often felt as heartburn, a sour taste, or a chronic cough) happens when stomach acid moves up into the oesophagus. Trigger foods vary, but common culprits in South African diets include coffee, chocolate, tomato-based sauces, fried foods, alcohol, and late-night meals.

Long-term reflux shouldn't be ignored. Beyond the discomfort, untreated reflux can damage the oesophagus.

## When a dietitian can help

Book a consultation if:

- Bloating or reflux is happening more than two or three times a week
- You've started avoiding foods or social events because of your gut
- Over-the-counter antacids are becoming a daily habit
- You've been told you have IBS, SIBO or functional dyspepsia and aren't sure what to eat
- You've tried "cutting out gluten" or "cutting out dairy" with no clear improvement

## What a dietitian visit looks like

A proper gut health consultation goes far beyond handing you a food list. We map your symptoms against meals, sleep, stress and bowel patterns, screen for red flags, and build a structured plan — often a short low-FODMAP trial, a reflux-friendly eating pattern, or a targeted reintroduction protocol.

If you're in Johannesburg and your gut is running the show, [book a consultation](/booking) or read more about my [gut health services](/services/gut-health).`,
  },
  {
    slug: "probiotics-prebiotics-microbiome-what-works",
    title: "Probiotics, Prebiotics & Your Microbiome: What Actually Works",
    metaTitle: "Probiotics South Africa | Gut Microbiome Guide | Functional Foodie",
    metaDescription: "A registered dietitian's evidence-based guide to probiotics, prebiotics and the gut microbiome — what works, what's hype, and what to buy in South Africa.",
    category: "Gut Health",
    date: "2026-06-05",
    excerpt: "Walk into any South African pharmacy and you'll see a wall of probiotics. Most do very little. Here's how to tell the difference — and why prebiotics often matter more.",
    keywords: ["probiotics South Africa", "gut microbiome", "prebiotics"],
    relatedServiceSlug: "gut-health",
    content: `The microbiome — the trillions of bacteria living in your gut — has gone from obscure research topic to wellness buzzword in under a decade. With that has come a flood of probiotic supplements, kombuchas and "gut-friendly" everything.

So what's actually worth your money?

## Probiotics: strain-specific, not magic

Probiotics are live bacteria that, in adequate amounts, confer a health benefit. The crucial word there is "adequate," and the second crucial point is that benefits are strain-specific — not species-specific.

Saying "I take a Lactobacillus" is like saying "I take a medication." Which strain, at what dose, for what condition?

Probiotics with good evidence in specific situations:

- Saccharomyces boulardii for antibiotic-associated diarrhoea
- Lactobacillus rhamnosus GG for acute gastroenteritis in children
- Specific multi-strain blends for IBS (research is mixed but promising)

For most healthy people with no symptoms, a daily probiotic is unlikely to make a noticeable difference.

## Prebiotics: the underrated hero

Prebiotics are the fibres that feed your existing good bacteria. Unlike probiotics, you don't need a supplement — they're abundant in everyday food:

- Oats, barley, rye
- Onions, garlic, leeks (yes, the same FODMAPs that bother IBS guts in large amounts)
- Bananas, especially slightly green
- Legumes — lentils, chickpeas, beans
- Cooked-and-cooled potatoes and rice (resistant starch)

If your goal is a robust microbiome, eating 30+ different plant foods per week has stronger evidence than any pill on the shelf.

## Fermented foods

Yoghurt, kefir, kimchi, sauerkraut and kombucha contain live cultures, plus the by-products of fermentation that may benefit gut lining health. They're a sensible addition to most diets — just check the label for actual live cultures and watch the sugar content of flavoured kombuchas.

## When supplements make sense

If you have IBS, IBD, are recovering from a course of antibiotics, or have specific symptoms, a targeted probiotic chosen by a [registered dietitian](/services/gut-health) is worth considering. Otherwise, your money is better spent on a varied, plant-rich diet.`,
  },
  {
    slug: "sustainable-weight-loss-why-crash-diets-fail-south-africans",
    title: "Sustainable Weight Loss: Why Crash Diets Fail South Africans (and What Works Instead)",
    metaTitle: "Sustainable Weight Loss Dietitian Johannesburg | Functional Foodie",
    metaDescription: "A registered dietitian in Johannesburg explains why crash diets fail and shares the evidence-based principles behind sustainable weight loss in South Africa.",
    category: "Weight Management",
    date: "2026-06-03",
    excerpt: "Almost everyone who crash-diets gains the weight back — often more. Here's what a registered dietitian recommends instead, in a South African food context.",
    keywords: ["weight loss dietitian Johannesburg", "sustainable weight loss", "weight management South Africa"],
    relatedServiceSlug: "weight-management",
    content: `If crash diets worked, no one would need to keep going on them. The research is unambiguous: somewhere between 80% and 95% of people who lose weight on a restrictive diet regain it within five years, and a meaningful portion end up heavier than when they started.

So why do we keep trying them? Because they feel decisive. And because the alternative — slow, boring, sustainable change — doesn't sell magazines.

## Why crash diets fail

- Severe calorie restriction lowers your metabolic rate, so you need fewer calories to maintain the same weight.
- Hunger hormones (ghrelin) rise and satiety hormones (leptin) fall, leaving you hungrier for months after the diet ends.
- Restriction breeds rebound eating. The "what the hell" effect after one slip is well documented.
- Most plans teach nothing transferable. When the meal plan ends, you go back to your old eating because no new habits were built.

## What actually works

Sustainable weight loss in South Africa looks much less dramatic than Instagram suggests:

- A modest calorie deficit (around 300–500 kcal/day) rather than a punishing one
- Adequate protein at every meal — typically 1.2–1.6 g/kg body weight
- Plenty of fibre from vegetables, fruit, legumes and whole grains
- A meal pattern you can imagine still doing in a year
- Strength training, even just two sessions a week, to preserve muscle
- Sleep of seven hours or more — sleep deprivation reliably increases appetite

## The South African context

Local food culture matters. A plan that ignores braais, Sunday lunches, rooibos with rusks, takeaways after long workdays, or the realities of load-shedding isn't going to last. A good dietitian works with your life, not against it.

## When to get help

If you've lost and regained the same 10kg more than twice, that's a strong signal to stop dieting alone and get structured support. [Book a weight management consultation](/booking) or read more about my [weight management approach](/services/weight-management).`,
  },
  {
    slug: "ozempic-glp1-dietitian-south-africa",
    title: "Ozempic, GLP-1s & Nutrition: A Dietitian's Honest Take",
    metaTitle: "Ozempic Dietitian South Africa | GLP-1 Nutrition Guide | Functional Foodie",
    metaDescription: "On Ozempic, Wegovy or another GLP-1 in South Africa? A registered dietitian explains how to eat to protect muscle, prevent deficiencies and keep results.",
    category: "Weight Management",
    date: "2026-06-01",
    excerpt: "GLP-1 medications like Ozempic and Wegovy are powerful tools, but they're not a substitute for a nutrition plan. Here's what a registered dietitian wants you to know.",
    keywords: ["Ozempic dietitian South Africa", "GLP-1 nutrition", "Wegovy dietitian"],
    relatedServiceSlug: "weight-management",
    content: `GLP-1 receptor agonists — Ozempic, Wegovy, Saxenda, Mounjaro — have changed the weight-loss conversation worldwide, and South Africa is no exception. They work, often dramatically. But the conversation around how to eat while on them is lagging far behind the prescriptions.

I'm not anti-GLP-1. I work with many clients using them. But I am firmly pro-nutrition-plan-alongside-them, for reasons that matter both now and once you stop.

## What GLP-1s do (and don't) do

GLP-1s slow gastric emptying, blunt appetite, and reduce "food noise." They do not:

- Choose nutrient-dense food for you
- Protect your muscle mass
- Build habits that survive after the injection stops
- Prevent the side effects that come from eating too little or too poorly

## The muscle mass problem

Studies on rapid weight loss — including on GLP-1s — show that up to 30–40% of the weight lost can be lean muscle if no protein or resistance training intervention is in place. That's a serious metabolic and functional cost, especially as you age.

The fix is straightforward but rarely emphasised at the prescription:

- 1.4–1.8 g protein per kg of body weight, daily, every day
- Two to three resistance training sessions per week
- Adequate (not excessive) calories — typically not below 1200 kcal for women or 1500 kcal for men without supervision

## Common nutrition pitfalls on GLP-1s

- Skipping meals because you're not hungry, then hitting the day at 400 calories
- High-protein-only days that leave you fibre-deficient and constipated
- Nausea-driven living on white toast and chicken
- Micronutrient gaps — particularly B12, iron, calcium and vitamin D

## After the medication

The harder question: what happens when you stop? Without behaviour change and a sustainable eating pattern in place, regain rates are high. The clients who keep the weight off long-term are the ones who used the medication window to build new habits.

If you're starting, on, or coming off a GLP-1 and want a nutrition plan built around it, [book a consultation](/booking). This is exactly the kind of work I do day in, day out.`,
  },
  {
    slug: "how-to-read-south-african-food-labels",
    title: "How to Read South African Food Labels Like a Dietitian",
    metaTitle: "How to Read Food Labels South Africa | Dietitian's Guide",
    metaDescription: "A registered dietitian's step-by-step guide to reading South African food labels — ingredients, nutrition tables, and marketing claims to ignore.",
    category: "Healthy Eating",
    date: "2026-05-28",
    excerpt: "\"High in fibre\", \"low GI\", \"natural\" — South African food packaging is full of claims. Here's how to cut through them and read what actually matters.",
    keywords: ["food labels South Africa", "healthy eating", "nutrition label guide"],
    content: `Walk down any South African supermarket aisle and you'll be promised wellness from every box, packet and bottle. "High in protein." "Low GI." "No added sugar." "Natural." Most of these claims are technically true and practically meaningless.

Here's the order I teach my clients to read a food label.

## 1. Ingredients list first, not the nutrition table

Ingredients are listed in descending order by weight. This is the single most useful part of any label.

- If sugar (or its many aliases — cane sugar, glucose syrup, fructose, dextrose, maltose, fruit juice concentrate) appears in the first three ingredients, it's a sugary product.
- If the ingredients list is longer than the back of your hand and full of words you don't recognise, it's an ultra-processed food.
- "Whole wheat" should appear before "wheat flour" if the product is genuinely whole-grain.

## 2. Then the nutrition table — per 100g, not per serving

South African labels show both "per 100g" and "per serving." Serving sizes are wildly inconsistent across brands. Always compare products using the per-100g column.

Quick reference for solid foods (per 100g):

- Sugar: under 5g is low; over 22.5g is high
- Saturated fat: under 1.5g is low; over 5g is high
- Salt (sodium): under 0.3g sodium is low; over 0.6g is high
- Fibre: above 6g is high (this is what you want)

## 3. Ignore the front of the box

The front of the package is marketing real estate. "Source of fibre" requires only 3g per 100g. "Natural" has no legal definition. "Low fat" can mean a product is loaded with sugar instead. "No added sugar" can still mean very high natural sugar (think fruit juice).

## 4. Watch for South African-specific traps

- Many "health" breakfast cereals here have 20–30g sugar per 100g
- "Lite" mayonnaise and salad dressings often replace fat with sugar
- Sports and energy drinks are routinely marketed as healthy hydration
- Flavoured yoghurts can have more sugar per pot than a chocolate bar

## 5. The plate test

If the food fits comfortably into the meal pattern I'd build for you in clinic — protein, plants, whole grains, some healthy fat — the label is mostly a formality. If you have to scrutinise the label to justify it, that itself is information.

Want personalised guidance for your shopping basket? [Book a nutrition counselling session](/booking).`,
  },
  {
    slug: "type-2-diabetes-meal-plan-south-africa",
    title: "Eating for Type 2 Diabetes: A South African Meal Plan Guide",
    metaTitle: "Diabetes Dietitian Johannesburg | Diabetic Meal Plan South Africa",
    metaDescription: "A registered dietitian in Johannesburg shares an evidence-based eating approach for Type 2 diabetes, using South African foods and realistic meals.",
    category: "Chronic Diseases",
    date: "2026-05-25",
    excerpt: "A Type 2 diabetes diagnosis isn't a sentence to bland food. Here's how a registered dietitian builds a flexible, South African-friendly eating plan that actually controls blood sugar.",
    keywords: ["diabetes dietitian Johannesburg", "diabetic meal plan South Africa", "type 2 diabetes diet"],
    relatedServiceSlug: "chronic-diseases",
    content: `Type 2 diabetes affects more than 4 million South Africans, and that number is climbing. The diagnosis is serious — but the dietary management is more flexible, and far less restrictive, than most newly diagnosed clients fear.

## The principle: manage carbohydrate load, don't eliminate it

Blood glucose is most influenced by the type and quantity of carbohydrate you eat. The goal is not "no carbs," it's "the right carbs, in the right amount, paired with the right partners."

## Build every meal on three pillars

1. Lean protein (palm-sized portion): chicken, fish, eggs, lean beef, tofu, cottage cheese.
2. Non-starchy vegetables (half your plate): spinach, broccoli, peppers, salad greens, cauliflower, courgettes.
3. A controlled portion of a low-GI carbohydrate (a cupped handful): oats, brown basmati rice, sweet potato, beans, lentils, low-GI seed bread.

Add a thumb of healthy fat — olive oil, avocado, nuts — to slow absorption further.

## A typical South African day

- Breakfast: rolled oats cooked with milk, topped with berries and a tablespoon of peanut butter; or two eggs with avocado on low-GI seed bread.
- Lunch: grilled chicken salad with chickpeas, mixed greens, feta and olive oil dressing.
- Snack: a small apple with a small handful of almonds.
- Dinner: baked hake with roasted butternut, broccoli and a green salad.

## Foods to be careful with

- Fruit juice and sweetened drinks — the fastest way to spike blood sugar
- White bread, white rice, mealie pap in large portions
- Many breakfast cereals — even "healthy" ones
- Cake, biscuits, sweets — occasional treats, not staples

## Beyond food

Diabetes management is not only nutrition. Movement after meals (even a 10-minute walk), stress management, sleep quality, and medication adherence all influence blood glucose. A good plan addresses all of these.

## When to involve a registered dietitian

If your HbA1c is rising, you're newly diagnosed, you're starting insulin, or you've been told to "just cut carbs" with no further guidance, an individualised plan will almost certainly outperform whatever you piece together online. [Book a consultation](/booking) or read more about my [chronic disease services](/services/chronic-diseases).`,
  },
  {
    slug: "lowering-cholesterol-through-diet-south-africa",
    title: "Lowering Cholesterol Through Diet: Evidence-Based Tips from a Registered Dietitian",
    metaTitle: "Lower Cholesterol Diet | Dietitian Johannesburg | Functional Foodie",
    metaDescription: "A registered dietitian in Johannesburg shares evidence-based dietary strategies to lower LDL cholesterol naturally, alongside any medication.",
    category: "Chronic Diseases",
    date: "2026-05-22",
    excerpt: "Diet can lower LDL cholesterol by 10–20% — comparable to a low-dose statin in many people. Here's what actually moves the needle, and what doesn't.",
    keywords: ["cholesterol diet", "heart healthy eating", "cholesterol dietitian"],
    relatedServiceSlug: "chronic-diseases",
    content: `If your last blood test came back with raised LDL cholesterol, you have more control than you may think. While genetics set the baseline, diet and lifestyle can shift LDL cholesterol by 10–20% — a meaningful change that can complement, or in some cases delay, medication.

## The four most effective dietary levers

1. **Soluble fibre.** Found in oats, barley, legumes, apples, pears, citrus and psyllium. Aim for 10g+ of soluble fibre daily. A bowl of oats plus a portion of beans plus a piece of fruit gets you there.
2. **Plant sterols and stanols.** Found in some fortified margarines and yoghurts (look for the labelling). 2g/day can lower LDL by around 10%.
3. **Unsaturated fats replacing saturated fats.** Swap butter, fatty meats and full-cream dairy for olive oil, avocado, nuts, seeds and oily fish.
4. **Soy and other legumes.** A daily portion of beans, lentils, chickpeas or soy products has a small but real LDL-lowering effect.

## The South African plate

- Choose oats or low-GI seed bread for breakfast, not refined cereals or white toast.
- Use olive oil for cooking and dressings; keep butter for occasional use.
- Eat oily fish (pilchards, sardines, salmon, mackerel) twice a week — pilchards are excellent value and locally available.
- Build at least one legume-based meal into the week — a chickpea curry, lentil bobotie, bean stew.
- Snack on a small handful of unsalted nuts most days.
- Limit processed meats — boerewors, polony, viennas — to occasional rather than daily.

## What doesn't move the needle much

- Cutting eggs. Dietary cholesterol has only a small effect on blood cholesterol in most people.
- Expensive "cholesterol-lowering" supplements with no published evidence.
- One healthy meal a week — consistency over weeks and months is what changes a blood test.

## Diet alongside medication

If your doctor has prescribed a statin or other lipid-lowering medication, take it — dietary change works with medication, not instead of it. Many clients are able to stay on lower doses or avoid escalation by combining the two.

For a personalised plan, [book a chronic disease consultation](/services/chronic-diseases) or [contact us](/contact).`,
  },
  {
    slug: "pcos-nutrition-dietitian-south-africa",
    title: "PCOS and Nutrition: How the Right Diet Can Help Manage Symptoms",
    metaTitle: "PCOS Dietitian South Africa | Nutrition Guide | Functional Foodie",
    metaDescription: "A registered dietitian in Johannesburg explains how nutrition can help manage PCOS symptoms — insulin resistance, weight, cycle regularity and skin.",
    category: "Chronic Diseases",
    date: "2026-05-19",
    excerpt: "PCOS isn't \"just\" a hormonal issue — at its core it's strongly tied to insulin resistance. The right nutrition approach can meaningfully shift symptoms.",
    keywords: ["PCOS dietitian South Africa", "PCOS diet", "polycystic ovary syndrome nutrition"],
    relatedServiceSlug: "chronic-diseases",
    content: `Polycystic Ovary Syndrome (PCOS) affects around 1 in 10 women of reproductive age. Symptoms vary, but commonly include irregular periods, weight gain (especially around the middle), acne, excess hair growth, and difficulty falling pregnant.

At the metabolic core of PCOS for most women is insulin resistance. That's also where nutrition has the most leverage.

## The nutrition principles that consistently help

- **Lower-GI, balanced meals.** Pair carbohydrates with protein and healthy fat at every meal to blunt the insulin response.
- **Adequate protein.** 1.2–1.6 g/kg body weight supports satiety, muscle mass and stable blood sugar.
- **Plenty of non-starchy vegetables.** Aim for half your plate at lunch and dinner.
- **Anti-inflammatory fats.** Oily fish, olive oil, nuts, seeds and avocado.
- **Limit ultra-processed foods and sugary drinks.** These spike insulin and worsen the underlying picture.
- **Consistent meal timing.** Long gaps followed by large meals tend to make insulin resistance worse.

## What about specific diets?

- **Low-carb diets** can be effective short-term but aren't necessary or sustainable for most women. A moderate, lower-GI approach usually works better long-term.
- **Mediterranean-style eating** has the strongest overall evidence for PCOS.
- **Inositol supplements** (myo- and d-chiro-inositol) have reasonable evidence for cycle regularity and insulin sensitivity; discuss with your doctor or dietitian.

## Movement matters

Resistance training, in particular, improves insulin sensitivity independently of weight loss. Two to three sessions per week is a realistic, high-impact target.

## The fertility angle

If you're trying to conceive with PCOS, modest weight loss (even 5%) often restores ovulation. Nutrition and lifestyle changes are first-line, evidence-based interventions — well before medication for most women.

## Working together

PCOS is lifelong but very manageable. A structured nutrition plan, ideally with a [registered dietitian](/services/chronic-diseases) who works with PCOS regularly, can meaningfully shift symptoms within months. [Book a consultation](/booking) to get started.`,
  },
  {
    slug: "pregnancy-nutrition-south-africa-trimester-guide",
    title: "Pregnancy Nutrition in South Africa: What to Eat (and Avoid) Trimester by Trimester",
    metaTitle: "Pregnancy Dietitian Johannesburg | Prenatal Nutrition Guide",
    metaDescription: "A registered dietitian in Johannesburg shares a trimester-by-trimester pregnancy nutrition guide using locally available South African foods.",
    category: "Lifecycle Nutrition",
    date: "2026-05-15",
    excerpt: "Pregnancy nutrition isn't about \"eating for two.\" It's about eating well for one and a bit. Here's a South African trimester-by-trimester guide from a registered dietitian.",
    keywords: ["pregnancy dietitian Johannesburg", "prenatal nutrition", "pregnancy diet South Africa"],
    relatedServiceSlug: "lifecycle-nutrition",
    content: `Pregnancy nutrition is one of the most rewarding areas of dietetic practice. The decisions you make over these nine months shape both your wellbeing and your baby's lifelong health — but it doesn't have to be complicated, and it certainly doesn't require eating for two.

## First trimester: foundations and nausea management

Energy needs barely change in the first trimester, but nutrient needs do. Priorities:

- **Folate (or folic acid):** 400 mcg daily, ideally started before conception. Found in dark leafy greens, legumes, fortified cereals.
- **Iodine:** important for baby's brain development. Use iodised salt.
- **Choline:** eggs are an excellent source.

If nausea is the issue (and it often is), forget the textbook plate. Small, frequent, dry, salty carbohydrates — provitas, dry toast, plain crackers — combined with ginger, often help. Eat what stays down.

## Second trimester: building

Energy needs rise by roughly 340 kcal/day — about a glass of milk and a sandwich. Focus areas:

- **Iron:** needs roughly double. Red meat, chicken, eggs, lentils, beans, fortified cereals. Pair plant sources with vitamin C (peppers, citrus, tomatoes) to boost absorption.
- **Calcium:** 1000 mg/day. Three servings of dairy, or fortified plant milks, plus leafy greens and tinned fish with bones.
- **Omega-3s (especially DHA):** oily fish twice a week. South African pilchards and sardines are excellent and affordable.

## Third trimester: growth and preparation

Energy needs rise a little more (around 450 kcal/day above pre-pregnancy). Protein needs are at their highest. Practical tips:

- Smaller, more frequent meals help with reflux and a squeezed stomach.
- Keep up the iron, calcium and omega-3 focus.
- Hydration matters more than ever — 2.3 litres a day is the rough target.

## Foods to avoid throughout

- Raw or undercooked meat, fish and eggs
- Soft, unpasteurised cheeses (some local artisan cheeses fall into this category)
- High-mercury fish (shark, swordfish, king mackerel — uncommon locally but worth knowing)
- Liver in large amounts (very high in vitamin A)
- Alcohol — no known safe amount
- Limit caffeine to 200 mg/day (roughly two small cups of filter coffee)

## When to see a dietitian

If you have pregnancy diabetes, hyperemesis, are vegetarian or vegan, are carrying multiples, or simply want personalised guidance, a [lifecycle nutrition consultation](/services/lifecycle-nutrition) can take the guesswork out of it. [Book here](/booking).`,
  },
  {
    slug: "dietitian-vs-nutritionist-south-africa",
    title: "Dietitian vs Nutritionist in South Africa: What's the Difference and Who Should You See?",
    metaTitle: "Dietitian vs Nutritionist South Africa | What's the Difference?",
    metaDescription: "Confused about the difference between a dietitian and a nutritionist in South Africa? A registered dietitian explains the qualifications, scope and when to see each.",
    category: "About Dietetics",
    date: "2026-05-12",
    excerpt: "In South Africa the titles \"dietitian\" and \"nutritionist\" are not interchangeable. Here's what each can — and legally can't — do, and how to choose.",
    keywords: ["dietitian vs nutritionist South Africa", "registered dietitian", "HPCSA dietitian"],
    content: `Walk into any health shop, gym or wellness Instagram feed and you'll find people offering nutrition advice under many titles: dietitian, nutritionist, nutrition coach, holistic health practitioner, functional nutritionist. In South Africa, these titles are not equivalent — and the differences matter, especially when you're dealing with a medical condition.

## Registered Dietitian (RD)

A registered dietitian in South Africa has:

- A four-year university degree in Dietetics (BSc Dietetics)
- A year of supervised community service
- Registration with the **Health Professions Council of South Africa (HPCSA)**
- A legal scope of practice that includes medical nutrition therapy

Dietitians are the only nutrition professionals in South Africa who can legally provide medical nutrition therapy — that is, dietary treatment for diagnosed medical conditions like diabetes, kidney disease, IBS, eating disorders, oncology and so on. Most medical aids only reimburse consultations with registered dietitians.

The title "dietitian" is legally protected. Using it without HPCSA registration is a criminal offence.

## Nutritionist

The title "nutritionist" is **not** legally protected in South Africa. That means:

- A nutritionist may hold a recognised university qualification — or a weekend online course. There is no way for you to tell from the title alone.
- Some are excellent, well-qualified practitioners working in wellness, sports or general healthy-eating advice.
- Others have very limited training and no regulatory body holding them accountable.

If you choose to see a nutritionist, ask about their qualifications, their professional registration (if any), and their scope of practice up front.

## When to see a registered dietitian

- Any diagnosed medical condition with a nutrition component — diabetes, high cholesterol, IBS, PCOS, kidney disease, food allergies, GORD
- Pregnancy, breastfeeding, or paediatric nutrition concerns
- Suspected or diagnosed eating disorder
- Significant weight management goals
- Anything requiring a medical aid claim

## When a well-qualified nutritionist may be enough

- General healthy eating support with no underlying medical condition
- Performance and body composition coaching alongside a coach or trainer

## How to check

Search the **HPCSA register** at hpcsa-online.com — every legitimate registered dietitian in South Africa appears there. You can also look for members of the **Association for Dietetics in South Africa (ADSA)** at adsa.org.za.

Functional Foodie is run by Dalya Hockman, a Registered Dietitian with the HPCSA and a member of ADSA. If you'd like to work together, [book a consultation](/booking) or [get in touch](/contact).`,
  },
];

export const getPostBySlug = (slug: string): BlogPost | undefined =>
  BLOG_POSTS.find((p) => p.slug === slug);

export const getSortedPosts = (): BlogPost[] =>
  [...BLOG_POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));